import { useEffect, useState, useCallback } from "react";
import apiCampaign from "../api";
import { campaignValidation } from "../features/campaign.validation";
import { useSettings } from "context/SettingsContext";
import { resolveDatePrefs, zonedDateTimeToUtc } from "services/date-converter";

const EMPTY_FORM = { campaign_name: "" };
const EMPTY_FILTER = {
  roles: [],
  company_fids: [],
  trade_of_work_fids: [],
  user_fids: [],
  login_filter: "ANY",
  login_from: null,
  login_to: null,
};

const STEPS = [
  { key: "details", label: "Details" },
  { key: "audience", label: "Audience" },
  { key: "recipients", label: "Recipients" },
  { key: "template", label: "Template" },
  { key: "schedule", label: "Schedule" },
  { key: "review", label: "Review" },
];

const hasLoginFilter = (f) =>
  f.login_filter === "CUSTOM_RANGE" ? Boolean(f.login_from || f.login_to) : Boolean(f.login_filter && f.login_filter !== "ANY");

const hasAnyFilter = (f) =>
  f.roles.length > 0 ||
  f.company_fids.length > 0 ||
  f.trade_of_work_fids.length > 0 ||
  f.user_fids.length > 0 ||
  hasLoginFilter(f);

export default function useCampaignWizard({ isOpen }) {
  const { settings } = useSettings() || {};
  const { timeZone } = resolveDatePrefs(settings);

  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const [audienceFilter, setAudienceFilter] = useState(EMPTY_FILTER);
  const [resolving, setResolving] = useState(false);
  const [resolvedRecipients, setResolvedRecipients] = useState([]);

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [sendMode, setSendMode] = useState("NOW");
  const [scheduledAt, setScheduledAt] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep(0);
    setErrors({});
    setFormData(EMPTY_FORM);
    setAudienceFilter(EMPTY_FILTER);
    setResolvedRecipients([]);
    setSelectedTemplate(null);
    setSendMode("NOW");
    setScheduledAt(null);
    setSubmitting(false);
  }, [isOpen]);

  const scheduledUtc = () =>
    scheduledAt
      ? zonedDateTimeToUtc(
          scheduledAt.getFullYear(),
          scheduledAt.getMonth() + 1,
          scheduledAt.getDate(),
          scheduledAt.getHours(),
          scheduledAt.getMinutes(),
          timeZone
        )
      : null;

  const setField = useCallback((name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
  }, []);

  const setFilterField = useCallback((name, value) => {
    setAudienceFilter((prev) => ({ ...prev, [name]: value }));
  }, []);

  const validateFields = (names) => {
    const nextErrors = {};
    names.forEach((name) => {
      const validator = campaignValidation[name];
      if (validator) {
        const msg = validator(formData[name]);
        if (msg) nextErrors[name] = msg;
      }
    });
    setErrors((prev) => ({ ...prev, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const removeRecipient = (id) => {
    setResolvedRecipients((prev) => prev.filter((r) => r.id !== id));
  };

  const canGoNextFromStep = (stepIndex) => {
    if (stepIndex === 0) return Boolean(formData.campaign_name?.trim());
    if (stepIndex === 1) return hasAnyFilter(audienceFilter);
    if (stepIndex === 2) return resolvedRecipients.length > 0;
    if (stepIndex === 3) return Boolean(selectedTemplate);
    if (stepIndex === 4) {
      if (sendMode === "NOW") return true;
      const utc = scheduledUtc();
      return Boolean(utc && utc.getTime() > Date.now());
    }
    return true;
  };

  const goNext = async () => {
    if (step === 0 && !validateFields(["campaign_name"])) return;

    if (step === 1) {
      if (!hasAnyFilter(audienceFilter)) return;
      setResolving(true);
      try {
        const toUtc = (d, h, m) =>
          d ? zonedDateTimeToUtc(d.getFullYear(), d.getMonth() + 1, d.getDate(), h, m, timeZone).toISOString() : null;
        const payload = {
          ...audienceFilter,
          login_from: audienceFilter.login_filter === "CUSTOM_RANGE" ? toUtc(audienceFilter.login_from, 0, 0) : null,
          login_to: audienceFilter.login_filter === "CUSTOM_RANGE" ? toUtc(audienceFilter.login_to, 23, 59) : null,
        };
        const res = await apiCampaign.resolveAudience(payload);
        setResolvedRecipients(res.data?.data || []);
      } finally {
        setResolving(false);
      }
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
      return;
    }

    if (!canGoNextFromStep(step)) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const finish = async () => {
    setSubmitting(true);
    try {
      const utc = sendMode === "SCHEDULED" ? scheduledUtc() : null;
      const payload = {
        campaign_name: formData.campaign_name.trim(),
        template_id: selectedTemplate.id,
        recipients: resolvedRecipients.map((r) => ({ user_fid: r.id, email: r.email })),
        scheduled_date: utc ? utc.toISOString() : null,
      };
      return await apiCampaign.create(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    STEPS,
    step,
    setStep,
    formData,
    setField,
    errors,
    canGoNext: canGoNextFromStep(step),
    goNext,
    goBack,

    audienceFilter,
    setFilterField,
    resolving,
    resolvedRecipients,
    removeRecipient,

    selectedTemplate,
    setSelectedTemplate,

    sendMode,
    setSendMode,
    scheduledAt,
    setScheduledAt,
    timeZone,

    submitting,
    finish,
  };
}
